import type { Building } from "building-types/building/buildingTypes";
import type { PorchColumnPattern } from "building-types/columns/porchColumnPattern";
import type { EdgeBehavior } from "building-types/edgeBehavior/edgeBehavior";
import type { EdgeDimensionBehavior } from "building-types/edgeBehavior/edgeDimensionBehavior";
import type { Label } from "building-types/facades/label";
import { LabelPrecedence } from "building-types/facades/label";
import type { FoundationAssemblyOverrides } from "building-types/foundations/foundationAssemblyEntityTypes";
import type { FoundationPattern } from "building-types/foundations/foundationPatternTypes";
import { NearestMeasurementMethod } from "building-types/foundations/foundationPatternTypes";
import type { Level } from "building-types/levels/levelTypes";
import type {
  RoomEdgeAssociation,
  LevelHalfEdgeAssociationTable,
  RWLevelHalfEdgeAssociationTable,
} from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomEnclosure } from "building-types/rooms/roomTypesEnum";
import type { WallAssemblyLayerOperator, WallAssemblyOperator } from "building-types/walls/wallAssemblyOverrideTypes";
import type { Table } from "common/data/table";
import { sortTableBySortIndex } from "common/utils/sortIndex";
import { ENTITY_ID_SEPARATOR } from "doc/entity/componentInstanceEntityResolve";
import { docEntityTableFilter } from "doc/entity/docEntityTableFilter";
import { line2EvalBump } from "geom/line2";
import type { EdgeRef, PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polyLine2Edge, polyLine2Reverse } from "geom/polyLine2";
import {
  findNearestHalfEdgeIndex,
  pierPositionRelativeToHalfEdge as positionRelativeToHalfEdge,
} from "../elements/foundation/autoFoundationSystem";
import { isIsolatedFoundationPattern, isolatedFoundationPatternNew } from "../elements/foundation/foundationPattern";
import { isWallAssemblyLayerOperator } from "../elements/wallAssemblies/wallAssemblyOverride";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";
import { porchColumnPatternNew } from "../utils/porchColumnPatternTools";

function select({
  levels,
  planarGraphs,
  rooms,
  roomLevelIndices,
  roomsByLevel,
  mirror,
  wallAssemblyEdgeBehaviors,
  dimensionEdgeBehaviors,
  porchColumnEdgeBehaviors,
  foundationAssemblyEdgeBehaviors,
  foundationPatternEdgeBehaviors,
  labelEdgeBehaviors,
}: Building) {
  return {
    rooms,
    levels,
    roomLevelIndices,
    roomsByLevel,
    planarGraphs,
    wallAssemblyEdgeBehaviors,
    porchColumnEdgeBehaviors,
    dimensionEdgeBehaviors,
    foundationAssemblyEdgeBehaviors,
    foundationPatternEdgeBehaviors,
    labelEdgeBehaviors,
    mirror,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  roomLevelIndices: "shallow",
  wallAssemblyEdgeBehaviors: "shallow",
  dimensionEdgeBehaviors: "shallow",
  porchColumnEdgeBehaviors: "shallow",
  foundationPatternEdgeBehaviors: "shallow",
  foundationAssemblyEdgeBehaviors: "shallow",
  labelEdgeBehaviors: "shallow",
});

function generate({
  rooms,
  levels,
  planarGraphs,
  roomLevelIndices,
  wallAssemblyEdgeBehaviors,
  dimensionEdgeBehaviors,
  porchColumnEdgeBehaviors,
  foundationAssemblyEdgeBehaviors,
  foundationPatternEdgeBehaviors,
  labelEdgeBehaviors,
  mirror,
}: ReturnType<typeof select>): Pick<
  Building,
  | "wallAssemblyOverrides"
  | "dimensionBehavior"
  | "porchColumnPatterns"
  | "foundationAssemblyOverrides"
  | "foundationPatterns"
  | "wallCuts"
  | "labels"
> {
  const roomEdgeAssociations: {
    labels: RoomEdgeAssociation<Label>[];
    wallAssemblies: RoomEdgeAssociation<WallAssemblyOperator>[];
    foundationPatterns: RoomEdgeAssociation<FoundationPattern>[];
    foundationAssemblies: RoomEdgeAssociation<FoundationAssemblyOverrides>[];
    porchColumnPatterns: RoomEdgeAssociation<PorchColumnPattern>[];
    dimensionBehavior: RoomEdgeAssociation<EdgeDimensionBehavior>[];
  } = {
    labels: [],
    wallAssemblies: [],
    foundationPatterns: [],
    foundationAssemblies: [],
    porchColumnPatterns: [],
    dimensionBehavior: [],
  };

  for (const roomId in rooms) {
    const room = rooms[roomId];

    // Edge behaviors are expanded onto instance rooms in generateEdgeBehaviors, so look up by
    // the room's own id (not the source/archetype room id).
    const roomWallAssemblyBehaviors = wallAssemblyEdgeBehaviors
      ? getRoomEdgeBehaviorsBySortIndex(wallAssemblyEdgeBehaviors, room.id)
      : undefined;
    for (const edgeWallAssemblyBehavior of roomWallAssemblyBehaviors || []) {
      if (!isWallAssemblyLayerOperator(edgeWallAssemblyBehavior.wallAssemblyOperator)) {
        roomEdgeAssociations.wallAssemblies.push({
          edgeIndex: room.edgeIds.indexOf(edgeWallAssemblyBehavior.edgeId),
          roomId: room.id,
          item: edgeWallAssemblyBehavior.wallAssemblyOperator,
          outside: edgeWallAssemblyBehavior.outside,
        });
      } else {
        const { wallAssemblyOperator, id } = edgeWallAssemblyBehavior;
        const { layer } = wallAssemblyOperator;
        const newLayer = {
          ...layer,
          parent: { type: "EDGE_BEHAVIOR", id },
          /**
           * @NOTE - this is a mandatory field on Operator layers.
           * The types do not reflect this and need to be updated
           */
          roomId,
        } as any;
        roomEdgeAssociations.wallAssemblies.push({
          edgeIndex: room.edgeIds.indexOf(edgeWallAssemblyBehavior.edgeId),
          roomId: room.id,
          item: { ...wallAssemblyOperator, layer: newLayer } as WallAssemblyLayerOperator,
          outside: edgeWallAssemblyBehavior.outside,
        });
      }
    }

    const roomDimensionBehaviors = getRoomEdgeBehaviorsBySortIndex(dimensionEdgeBehaviors, room.id);
    for (const edgeDimensionBehavior of roomDimensionBehaviors) {
      const item: EdgeDimensionBehavior = {
        disableExteriorAuto: edgeDimensionBehavior.disableExteriorAuto,
        disableInteriorAuto: edgeDimensionBehavior.disableInteriorAuto,
        disableDiagonalAuto: edgeDimensionBehavior.disableDiagonalAuto,
      };
      roomEdgeAssociations.dimensionBehavior.push({
        edgeIndex: room.edgeIds.indexOf(edgeDimensionBehavior.edgeId),
        roomId: room.id,
        item,
      });
    }

    const porchColumnPatterns = getRoomEdgeBehaviorsBySortIndex(porchColumnEdgeBehaviors, room.id);
    for (const porchColumnPattern of porchColumnPatterns) {
      roomEdgeAssociations.porchColumnPatterns.push({
        edgeIndex: room.edgeIds.indexOf(porchColumnPattern.edgeId),
        roomId: room.id,
        item: porchColumnPattern.porchColumnPattern,
      });
    }

    const foundationAssemblyOverrides = getRoomEdgeBehaviorsBySortIndex(foundationAssemblyEdgeBehaviors, room.id);
    for (const foundationAssembly of foundationAssemblyOverrides) {
      roomEdgeAssociations.foundationAssemblies.push({
        edgeIndex: room.edgeIds.indexOf(foundationAssembly.edgeId),
        roomId: room.id,
        item: foundationAssembly.foundationAssemblyOverrides,
      });
    }

    const foundationPatterns = getRoomEdgeBehaviorsBySortIndex(foundationPatternEdgeBehaviors, room.id);
    for (const foundationPatternBehavior of foundationPatterns) {
      const pattern = foundationPatternBehavior.foundationPattern;
      const itemWithEdgeBehaviorId = isIsolatedFoundationPattern(pattern)
        ? { ...pattern, edgeBehaviorId: foundationPatternBehavior.id }
        : pattern;
      roomEdgeAssociations.foundationPatterns.push({
        edgeIndex: room.edgeIds.indexOf(foundationPatternBehavior.edgeId),
        roomId: room.id,
        item: itemWithEdgeBehaviorId,
      });
    }

    const edgeBehaviors = getRoomEdgeBehaviorsBySortIndex(labelEdgeBehaviors, room.id);
    for (const edgeBehavior of edgeBehaviors) {
      let facadeId = edgeBehavior.facadeId;
      const rootInstanceId = room.componentTransform?.componentInstanceChain[0];
      if (rootInstanceId) {
        facadeId = `${facadeId}${ENTITY_ID_SEPARATOR}${rootInstanceId}`;
      }
      roomEdgeAssociations.labels.push({
        edgeIndex: room.edgeIds.indexOf(edgeBehavior.edgeId),
        roomId: room.id,
        item: { facadeId },
        outside: true,
      });
    }
  }

  return {
    wallCuts: convertRoomEdgeToHalfEdgeAssociation(levels, [], rooms, planarGraphs, roomLevelIndices),
    labels: convertRoomEdgeToHalfEdgeAssociation(
      levels,
      roomEdgeAssociations.labels,
      rooms,
      planarGraphs,
      roomLevelIndices,
    ),
    wallAssemblyOverrides: convertRoomEdgeToHalfEdgeAssociation(
      levels,
      roomEdgeAssociations.wallAssemblies,
      rooms,
      planarGraphs,
      roomLevelIndices,
    ),
    foundationPatterns: convertRoomEdgeFoundationPatternsToHalfEdgeAssociation(
      levels,
      roomEdgeAssociations.foundationPatterns,
      rooms,
      planarGraphs,
      mirror,
      roomLevelIndices,
    ),
    foundationAssemblyOverrides: convertRoomEdgeToHalfEdgeAssociation(
      levels,
      roomEdgeAssociations.foundationAssemblies,
      rooms,
      planarGraphs,
      roomLevelIndices,
    ),
    porchColumnPatterns: convertRoomEdgeLayoutColumnPatternsToHalfEdgeAssociation(
      levels,
      roomEdgeAssociations.porchColumnPatterns,
      rooms,
      planarGraphs,
      mirror,
      roomLevelIndices,
    ),
    dimensionBehavior: convertRoomEdgeToHalfEdgeAssociation(
      levels,
      roomEdgeAssociations.dimensionBehavior,
      rooms,
      planarGraphs,
      roomLevelIndices,
    ),
  };
}

function getRoomEdgeBehaviorsBySortIndex<T extends EdgeBehavior>(edgeBehaviors: Table<T>, roomId: string) {
  const { newTable: roomEdgeBehaviors } = docEntityTableFilter(edgeBehaviors, (x) => x.parentId === roomId);
  return sortTableBySortIndex(roomEdgeBehaviors) as T[];
}

function convertRoomEdgeToHalfEdgeAssociation<T>(
  levels: Table<Level>,
  roomEdgeAssociations: RoomEdgeAssociation<T>[],
  rooms: Table<Room>,
  planarGraphs: Table<PolygonPlanarGraph>,
  roomLevelIndices: Table<number>,
): LevelHalfEdgeAssociationTable<T> {
  const halfEdgeAssociations: RWLevelHalfEdgeAssociationTable<T> = {};

  for (const levelId in levels) {
    halfEdgeAssociations[levelId] = {};
  }

  if (!roomEdgeAssociations) {
    return halfEdgeAssociations;
  }

  for (const { item, edgeIndex, roomId, subWallIndices, outside } of roomEdgeAssociations) {
    const levelId = rooms[roomId].level.id;
    const planarGraph = planarGraphs[levelId];

    const halfEdgeIndices = getHalfEdgesFromRoomEdge(
      false,
      planarGraph,
      roomLevelIndices,
      roomId,
      edgeIndex,
      subWallIndices,
    );

    const table = halfEdgeAssociations[levelId];

    for (const halfEdgeIndex of halfEdgeIndices) {
      const values = table[halfEdgeIndex] || [];
      values.push({ item, roomId, outside: !!outside, precedence: LabelPrecedence.Room });
      table[halfEdgeIndex] = values;
    }
  }

  return halfEdgeAssociations;
}

function convertRoomEdgeFoundationPatternsToHalfEdgeAssociation(
  levels: Table<Level>,
  roomEdgeAssociations: RoomEdgeAssociation<FoundationPattern>[],
  rooms: Table<Room>,
  planarGraphs: Table<PolygonPlanarGraph>,
  mirror: boolean,
  roomLevelIndices: Table<number>,
): LevelHalfEdgeAssociationTable<FoundationPattern> {
  const halfEdgeAssociations: RWLevelHalfEdgeAssociationTable<FoundationPattern> = {};

  for (const levelId in levels) {
    halfEdgeAssociations[levelId] = {};
  }

  if (!roomEdgeAssociations) {
    return halfEdgeAssociations;
  }

  for (const { item, edgeIndex, roomId, subWallIndices, outside } of roomEdgeAssociations) {
    const levelId = rooms[roomId].level.id;
    const planarGraph = planarGraphs[levelId];

    const halfEdgeIndices = getHalfEdgesFromRoomEdge(
      false,
      planarGraph,
      roomLevelIndices,
      roomId,
      edgeIndex,
      subWallIndices,
    );

    const table = halfEdgeAssociations[levelId];

    if (isIsolatedFoundationPattern(item)) {
      const room = rooms[roomId];
      const edge = polyLine2Edge(room.outline, edgeIndex);
      if (mirror) {
        polyLine2Reverse(edge);
      }
      for (const position of item.positions) {
        const [x, y] = line2EvalBump(
          edge[0],
          edge[1],
          edge[2],
          edge[3],
          position.param,
          position.paramNormalized,
          item.xOffset,
        );
        const halfEdgeIndex = findNearestHalfEdgeIndex(halfEdgeIndices, planarGraph.planarGraph, x, y);
        if (halfEdgeIndex === undefined) {
          continue;
        }
        const values = table[halfEdgeIndex] || [];
        const newPierPosition = positionRelativeToHalfEdge(halfEdgeIndex, planarGraph.planarGraph, x, y);
        const newItem = isolatedFoundationPatternNew({
          ...item,
          xOffset: 0,
          positions: [{ ...position, ...newPierPosition }],
          topDatumId: item.topDatumId,
          bottomDatumId: item.bottomDatumId,
        });
        values.push({
          item: newItem as FoundationPattern,
          roomId,
          outside: !!outside,
          precedence: LabelPrecedence.Room,
        });
        table[halfEdgeIndex] = values;
      }
    } else {
      for (const halfEdgeIndex of halfEdgeIndices) {
        const values = table[halfEdgeIndex] || [];
        values.push({ item, roomId, outside: !!outside, precedence: LabelPrecedence.Room });
        table[halfEdgeIndex] = values;
      }
    }
  }

  return halfEdgeAssociations;
}

function convertRoomEdgeLayoutColumnPatternsToHalfEdgeAssociation(
  levels: Table<Level>,
  roomEdgeAssociations: RoomEdgeAssociation<PorchColumnPattern>[],
  rooms: Table<Room>,
  planarGraphs: Table<PolygonPlanarGraph>,
  mirror: boolean,
  roomLevelIndices: Table<number>,
): LevelHalfEdgeAssociationTable<PorchColumnPattern> {
  const halfEdgeAssociations: RWLevelHalfEdgeAssociationTable<PorchColumnPattern> = {};

  for (const levelId in levels) {
    halfEdgeAssociations[levelId] = {};
  }

  if (!roomEdgeAssociations) {
    return halfEdgeAssociations;
  }

  for (const { item, edgeIndex, roomId, subWallIndices, outside } of roomEdgeAssociations) {
    const room = rooms[roomId];
    const isRoomEnclosureNone = room.attributes.roomEnclosure === RoomEnclosure.NONE;
    const levelId = room.level.id;
    const planarGraph = planarGraphs[levelId];

    const halfEdgeIndices = getHalfEdgesFromRoomEdge(
      false,
      planarGraph,
      roomLevelIndices,
      roomId,
      edgeIndex,
      subWallIndices,
    );

    const table = halfEdgeAssociations[levelId];
    const emptyItem = porchColumnPatternNew([]);

    if (halfEdgeIndices.length > 1) {
      //mapping needs to be done
      const edge = polyLine2Edge(room.outline, edgeIndex);
      if (mirror) {
        polyLine2Reverse(edge);
      }
      const edgeHalfEdgePositionsCounter: { [id: number]: number } = {};
      halfEdgeIndices.map((halfEdgeIndex) => (edgeHalfEdgePositionsCounter[halfEdgeIndex] = 0));
      for (const position of item.positions) {
        const [x, y] = line2EvalBump(edge[0], edge[1], edge[2], edge[3], position.param, position.paramNormalized, 0);
        const halfEdgeIndex = findNearestHalfEdgeIndex(
          halfEdgeIndices,
          planarGraph.planarGraph,
          x,
          y,
          NearestMeasurementMethod.PERP_DIST,
          true,
          true,
        );
        if (halfEdgeIndex === undefined) {
          continue;
        }
        const values = table[halfEdgeIndex] || [];
        const columnPosition = positionRelativeToHalfEdge(halfEdgeIndex, planarGraph.planarGraph, x, y, mirror);
        const newItem = isRoomEnclosureNone ? emptyItem : porchColumnPatternNew([columnPosition]);
        values.push({
          item: newItem as PorchColumnPattern,
          roomId,
          outside: !!outside,
          precedence: LabelPrecedence.Room,
        });
        table[halfEdgeIndex] = values;
        edgeHalfEdgePositionsCounter[halfEdgeIndex]++;
      }
      //insert empty columns for halfedges that have no column - ovveride porch column default behaviour
      for (const halfEdgeIndex of halfEdgeIndices) {
        if (edgeHalfEdgePositionsCounter[halfEdgeIndex] === 0) {
          const values = table[halfEdgeIndex] || [];
          const newItem = porchColumnPatternNew([]);
          values.push({
            item: newItem as PorchColumnPattern,
            roomId,
            outside: !!outside,
            precedence: LabelPrecedence.Room,
          });
          table[halfEdgeIndex] = values;
        }
      }
    } else {
      for (const halfEdgeIndex of halfEdgeIndices) {
        const values = table[halfEdgeIndex] || [];
        values.push({
          item: isRoomEnclosureNone ? emptyItem : item,
          roomId,
          outside: !!outside,
          precedence: LabelPrecedence.Room,
        });
        table[halfEdgeIndex] = values;
      }
    }
  }
  return halfEdgeAssociations;
}

export function getHalfEdgesFromRoomEdge(
  mirror: boolean,
  planarGraph: PolygonPlanarGraph,
  roomLevelIndices: Table<number>,
  roomId: string,
  edgeIndex: number,
  subWallIndices?: number[],
): number[] {
  const roomIndexOnLevel = roomLevelIndices[roomId];
  const roomIndices = planarGraph.polygonToPlanarGraph[roomIndexOnLevel];
  if (mirror && edgeIndex !== 0) {
    edgeIndex = roomIndices.length - edgeIndex;
  }

  if (edgeIndex === -1) {
    return [];
  }

  const subWalls = planarGraph.polygonToPlanarGraph[roomIndexOnLevel][edgeIndex];

  if (!(subWalls !== undefined)) {
    console.assert(false, `could not find edge index given ${edgeIndex}`);
  }
  const subWallsLength = subWalls.length - 1;
  if (mirror && subWallIndices) {
    const newSubWallIndices: number[] = new Array(subWallIndices.length);
    for (const index of Object.keys(subWallIndices)) {
      const subWallIndex = subWallIndices[index];
      newSubWallIndices[index] = subWallsLength - subWallIndex;
    }
    subWallIndices = newSubWallIndices;
  }

  function addSubWallHalfEdge(subWall: EdgeRef) {
    const { index, edgeIndex: subEdgeIndex } = subWall;
    const halfEdgeIndex = planarGraph.planarGraph.faceHalfEdges[index][subEdgeIndex];
    halfEdgeIndices.push(halfEdgeIndex);
  }

  const halfEdgeIndices: number[] = [];
  if (subWallIndices) {
    for (const subWallIndex of subWallIndices) {
      if (subWallIndex > subWalls.length - 1) {
        console.warn("cannot assign to subWall index outside of the subWall array size");
        continue;
      }
      const subWall = subWalls[subWallIndex];
      addSubWallHalfEdge(subWall);
    }
  } else {
    for (const subWall of subWalls) {
      addSubWallHalfEdge(subWall);
    }
  }
  return halfEdgeIndices;
}

export default memo(select, generate, compare);
